import { FormattedContacts, PoEmailData } from '../../../models/poData/FormattedContacts';
import { ContactInsight } from '../../../models/poData/ContactInsight';
import { isDefaultChecked } from './contact-insight.rules';
import { looksLikeEmailAddress } from './generate-email';

/**
 * Determines the primary contact for a receiving log from the PO's contact information.
 *
 * If the PO has no explicit notify list but does have contact emails, the first contact
 * email becomes the primary contact. Otherwise no primary contact is assigned.
 *
 * @param contactInformation - Formatted contact data from the PO.
 * @returns The primary contact email, or an empty string if none applies.
 */
export function derivePrimaryContact(contactInformation: FormattedContacts): string {
  const contactEmailList = contactInformation.allContactEmails;
  if (contactInformation.notify.length === 0 && contactEmailList.length > 0) {
    return contactEmailList[0].contact;
  }
  return '';
}

/**
 * Finds a PO contact's BambooHR insight by address.
 *
 * Searches the flattened list first, then the individually named fields — the
 * user can star a contact (creator / requested-by / attention) that never made it
 * into `allContactEmails`, and its insight lives only on the named property.
 * Returns undefined for an address the PO didn't carry, e.g. one the user added
 * from the employee search.
 *
 * @param contactInformation - Formatted contact data from the PO.
 * @param email - The address to look up (compared case-insensitively).
 */
export function findContactInsight(
  contactInformation: FormattedContacts | null | undefined,
  email: string
): ContactInsight | undefined {
  if (!contactInformation || !email) { return undefined; }
  const key = email.trim().toLowerCase();

  return allContactEntries(contactInformation)
    .find(c => c?.contact?.trim().toLowerCase() === key && c.insight)?.insight;
}

/**
 * Whether the PO named a contact we have no address for — a GP value that was a
 * name, a name with a phone number, or an off-domain address.
 *
 * Nothing is guessed for these (see `generateEmail`), so they cannot be emailed
 * or printed as-is. Drives the prompt telling the user to pick the real person
 * out of the employee directory, and read off the contacts themselves rather than
 * the PO's data source — a GP PO whose contacts all arrived as real addresses has
 * nothing to fix.
 *
 * @param contactInformation - Formatted contact data from the PO.
 */
export function hasContactNeedingEmail(contactInformation: FormattedContacts | null | undefined): boolean {
  if (!contactInformation) { return false; }
  return allContactEntries(contactInformation)
    .some(c => !!c?.contact && !looksLikeEmailAddress(c.contact));
}

/**
 * Every contact entry a PO carries: the flattened list plus the individually
 * named fields, which hold contacts that never made it into `allContactEmails`
 * (only well-formed addresses are pushed there). May contain duplicates.
 */
function allContactEntries(contactInformation: FormattedContacts): (PoEmailData | undefined)[] {
  return [
    ...(contactInformation.allContactEmails || []),
    ...(contactInformation.notify || []),
    contactInformation.poCreator,
    contactInformation.shipToAttention,
    contactInformation.requestedBy,
  ];
}

/**
 * The contact state a receiving log entry starts with after a PO lookup: who is
 * pre-checked to be emailed, and which of them is the main contact.
 */
export interface InitialContacts {
  /** Contacts pre-checked to receive the notification email. */
  notifyList: PoEmailData[];
  /** Comma-separated form of {@link InitialContacts.notifyList}. */
  notifyListString: string;
  /**
   * The main contact — printed on the labels/sheets and written to the log's
   * Email field — or '' when the user has to pick one.
   */
  primaryContact: string;
}

/**
 * Derives the starting notify list and main contact from a PO's contacts.
 *
 * Pre-checks ONLY the contacts BambooHR insights vouch for as employees
 * (ado-11284). Internal non-employees (groups / mailing lists), external
 * addresses, and insight-less contacts stay visible in `allContactEmails` for
 * the user to opt into, but start unchecked. With BambooHR unavailable nothing
 * pre-checks.
 *
 * The main contact has to be someone we're actually emailing, so the derived
 * primary is kept only when it survived that filter. A single pre-checked
 * employee is starred outright — there's nothing to choose between. With several,
 * the primary is usually left unset for the user to star.
 *
 * Shared by both UIs so they can't drift: the desktop form (through
 * `derivePoPrefill`) and the mobile flow's shell both start from this.
 *
 * @param contactInformation - Formatted contact data from the PO.
 * @returns The pre-checked notify list, its string form, and the main contact.
 */
export function deriveInitialContacts(contactInformation: FormattedContacts): InitialContacts {
  const notifyList = (contactInformation.allContactEmails || []).filter(e => isDefaultChecked(e.insight));
  const notifyListString = generateNotifyListString(notifyList);

  // Counted on distinct addresses: one person who is both the PO creator and the
  // requested-by is pushed onto allContactEmails twice.
  const distinctContacts = new Set(notifyList.map(e => (e.contact || '').trim().toLowerCase()));

  // Exactly one employee on the PO is unambiguous — star them rather than make the
  // user confirm a choice of one.
  if (distinctContacts.size === 1) {
    return { notifyList, notifyListString, primaryContact: notifyList[0].contact };
  }

  // Nothing pre-checked means nobody is eligible to lead, so skip the lookup —
  // which also keeps this safe when the PO carried no contact emails at all.
  const derivedPrimary = notifyList.length ? derivePrimaryContact(contactInformation) : '';

  return {
    notifyList,
    notifyListString,
    primaryContact: notifyList.some(e => e.contact === derivedPrimary) ? derivedPrimary : '',
  };
}

/**
 * Generates a comma-separated string of contact emails from the notify list.
 *
 * @param notifyList - The list of email data objects.
 * @returns A comma-separated string of contact emails.
 */
export function generateNotifyListString(notifyList: PoEmailData[]): string {
  // Check if the notifyList is an array
  if (!Array.isArray(notifyList)) {
    console.error('notifyList is not an array:', notifyList);
    return '';
  }

  // Filter out invalid contacts and map to their email addresses
  return notifyList
    .filter((contact) => contact && contact.contact) // Ensure each object has a contact property
    .map((contact) => contact.contact) // Extract the contact email
    .join(', '); // Join the emails into a comma-separated string
}

/**
 * Splits a comma-separated notify string into a clean list of recipient emails.
 *
 * @param notifyListString - Comma-separated email string (possibly empty).
 * @returns An array of trimmed, non-empty email addresses.
 */
export function parseRecipients(notifyListString: string): string[] {
  return notifyListString
    ? notifyListString
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email)
    : [];
}
